
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { getUserCredits } from '../services/api';
import { purchaseCredits, CREDIT_PACKS } from '../services/stripeService';
import { USE_REAL_STRIPE } from '../services/stripeApi';
import Swal from 'sweetalert2';
import { User, Mail, Phone, MapPin, Lock, CreditCard, Save, Eye, EyeOff, Trash2, Home, Heart } from 'lucide-react';
import { translateError } from '../utils/errorTranslator';

function useMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
}

export default function UserSettings({ session }) {
  const isMobile = useMobile();
  const [userProfile, setUserProfile] = useState({
    email: session?.user?.email || '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    zipCode: '',
    city: '',
    credits: 0
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    // Gestion du message de succès après retour de Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) {
      Swal.fire({
        icon: 'success',
        title: 'Paiement réussi !',
        text: 'Vos crédits ont été ajoutés à votre compte.',
        timer: 5000,
        timerProgressBar: true
      });
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Charger les informations utilisateur depuis Supabase
    const loadUserProfile = async () => {
      if (!session?.user?.id) return;

      try {
        // Charger le profil
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, phone, address, zip_code, city')
          .eq('id', session.user.id)
          .single();

        // Charger le total des crédits
        const totalCredits = await getUserCredits(session.user.id);

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Erreur chargement profil:', profileError);
        }

        setUserProfile({
          email: session.user.email,
          firstName: profile?.first_name || '',
          lastName: profile?.last_name || '',
          phone: profile?.phone || '',
          address: profile?.address || '',
          zipCode: profile?.zip_code || '',
          city: profile?.city || '',
          credits: totalCredits
        });
      } catch (error) {
        console.error('Erreur:', error);
      }
    };

    loadUserProfile();
  }, [session]);

  const handleProfileChange = (field, value) => {
    setUserProfile(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleUpdateProfile = async () => {
    if (!userProfile.firstName.trim() || !userProfile.lastName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Champs requis',
        text: 'Veuillez saisir votre prénom et nom.'
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: userProfile.firstName.trim(),
          last_name: userProfile.lastName.trim(),
          phone: userProfile.phone.trim(),
          address: userProfile.address.trim(),
          zip_code: userProfile.zipCode.trim(),
          city: userProfile.city.trim()
        })
        .eq('id', session.user.id);

      if (error) throw error;

      Swal.fire({
        icon: 'success',
        title: 'Profil mis à jour',
        text: 'Vos informations ont été sauvegardées.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: translateError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Champs requis',
        text: 'Veuillez remplir tous les champs du mot de passe.'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Erreur',
        text: 'Les nouveaux mots de passe ne correspondent pas.'
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Mot de passe trop court',
        text: 'Le mot de passe doit contenir au moins 6 caractères.'
      });
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      // Réinitialiser le formulaire
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      Swal.fire({
        icon: 'success',
        title: 'Mot de passe modifié',
        text: 'Votre mot de passe a été changé avec succès.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: translateError(error)
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDonation = async () => {
    const { value: amount } = await Swal.fire({
      title: 'Soutenir SavatEvent',
      html: `
        <p style="margin-bottom: 25px; color: #4a5568;">Choisissez le montant de votre contribution pour nous aider à maintenir l'application gratuite.</p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px;">
          <button class="donation-btn" data-amount="500">5 €</button>
          <button class="donation-btn" data-amount="1000">10 €</button>
          <button class="donation-btn" data-amount="2000">20 €</button>
          <button class="donation-btn" data-amount="5000">50 €</button>
        </div>
        <div style="margin-top: 10px; text-align: left;">
          <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #4a5568;">Ou montant libre (€) :</label>
          <input type="number" id="custom-amount" class="swal2-input" placeholder="Ex: 15" style="margin: 0; width: 100%; box-sizing: border-box;">
        </div>
        <style>
          .donation-btn {
            background: white;
            border: 2px solid #e2e8f0;
            padding: 20px;
            border-radius: 12px;
            font-size: 20px;
            font-weight: bold;
            color: #2d3748;
            cursor: pointer;
            transition: all 0.2s;
          }
          .donation-btn:hover {
            border-color: #3182ce;
            background: #ebf8ff;
            color: #3182ce;
            transform: translateY(-2px);
          }
          .donation-btn.active {
            border-color: #3182ce;
            background: #ebf8ff;
            color: #3182ce;
          }
        </style>
      `,
      showCancelButton: true,
      cancelButtonText: 'Annuler',
      confirmButtonText: 'Confirmer le don',
      confirmButtonColor: '#3182ce',
      preConfirm: () => {
        const customAmount = document.getElementById('custom-amount').value;
        const activeBtn = document.querySelector('.donation-btn.active');
        
        let finalAmount;
        if (customAmount) {
          finalAmount = parseFloat(customAmount) * 100;
        } else if (activeBtn) {
          finalAmount = parseInt(activeBtn.getAttribute('data-amount'));
        }

        if (!finalAmount || finalAmount <= 0) {
          Swal.showValidationMessage('Veuillez choisir ou saisir un montant valide');
          return false;
        }
        return finalAmount;
      },
      didOpen: () => {
        const btns = document.querySelectorAll('.donation-btn');
        const customInput = document.getElementById('custom-amount');

        btns.forEach(btn => {
          btn.addEventListener('click', () => {
            btns.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            customInput.value = ''; // Clear custom if button clicked
          });
        });

        customInput.addEventListener('input', () => {
          if (customInput.value) {
            btns.forEach(b => b.classList.remove('active'));
          }
        });
      }
    });

    if (amount) {
      try {
        setLoading(true);
        const amtNum = parseInt(amount);
        
        // On crée manuellement les données de paiement pour l'Edge Function
        const { createStripePaymentSession } = await import('../services/stripeApi');
        const stripeSession = await createStripePaymentSession({
          packId: 'donation_' + amount,
          packName: 'Don de soutien',
          userId: session.user.id,
          userEmail: session.user.email,
          credits: 0,
          amount: amtNum
        });

        if (stripeSession?.url) {
          window.location.href = stripeSession.url;
        }
      } catch (error) {
        console.error('Erreur don:', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de procéder au don.' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePurchaseCredits = async (packId) => {
    // Bloqué en Beta
    Swal.fire({
        title: 'Phase Bêta',
        text: 'Les achats directs de crédits sont désactivés pendant la phase bêta. Vous pouvez créer des compétitions librement.',
        icon: 'info'
    });
    return;

    try {
      setLoading(true);

      await purchaseCredits(packId, session.user.id, session.user.email);

      // Rafraîchir les crédits après l'achat réussi
      const updatedCredits = await getUserCredits(session.user.id);
      setUserProfile(prev => ({ ...prev, credits: updatedCredits }));

    } catch (error) {
      console.error('Erreur achat crédits:', error);
      if (error.message !== 'Paiement annulé par l\'utilisateur') {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de procéder au paiement. Veuillez réessayer.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 15px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.2s'
  };

  const disabledInputStyle = {
    ...inputStyle,
    backgroundColor: '#f7fafc',
    cursor: 'not-allowed'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#2d3748',
    fontSize: '14px'
  };

  const sectionStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };

  const sectionTitleStyle = {
    margin: '0 0 20px 0',
    color: '#1a202c',
    fontSize: '18px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  return (
    <div style={{ padding: '20px', maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <h1 style={{ margin: '0 0 8px 0', color: '#1a202c', fontSize: '28px', fontWeight: 'bold' }}>
          Réglages du compte
        </h1>
        <p style={{ margin: 0, color: '#718096', fontSize: '16px' }}>
          Gérez vos informations personnelles et votre sécurité
        </p>
      </div>

      {/* Section Informations personnelles */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <User size={20} color="#3182ce" />
          Informations personnelles
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Prénom</label>
            <input
              type="text"
              value={userProfile.firstName}
              onChange={(e) => handleProfileChange('firstName', e.target.value)}
              placeholder="Votre prénom"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Nom</label>
            <input
              type="text"
              value={userProfile.lastName}
              onChange={(e) => handleProfileChange('lastName', e.target.value)}
              placeholder="Votre nom"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Téléphone</label>
            <input
              type="text"
              value={userProfile.phone}
              onChange={(e) => handleProfileChange('phone', e.target.value)}
              placeholder="06 00 00 00 00"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Adresse postale (Facturation)</label>
            <input
              type="text"
              value={userProfile.address}
              onChange={(e) => handleProfileChange('address', e.target.value)}
              placeholder="Rue, numéro"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Code Postal</label>
            <input
              type="text"
              value={userProfile.zipCode}
              onChange={(e) => handleProfileChange('zipCode', e.target.value)}
              placeholder="75000"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Ville</label>
            <input
              type="text"
              value={userProfile.city}
              onChange={(e) => handleProfileChange('city', e.target.value)}
              placeholder="Votre ville"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Adresse email</label>
          <input
            type="email"
            value={userProfile.email}
            disabled
            style={disabledInputStyle}
          />
          <small style={{ color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            L'adresse email ne peut pas être modifiée
          </small>
        </div>

        <button
          onClick={handleUpdateProfile}
          disabled={loading}
          style={{
            backgroundColor: loading ? '#a0aec0' : '#3182ce',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center', gap: '8px'
          }}
        >
          <Save size={16} />
          {loading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
        </button>
      </div>

      {/* Section Crédits disponibles */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <CreditCard size={20} color="#38a169" />
          Crédits disponibles
        </h3>

        <div style={{
          backgroundColor: '#f0fff4',
          border: '2px solid #9ae6b4',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: '#38a169',
            marginBottom: '8px'
          }}>
            {userProfile.credits}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#2f855a',
            fontWeight: '600'
          }}>
            crédits disponibles
          </div>
          <p style={{
            margin: '16px 0 0 0',
            color: '#4a5568',
            fontSize: '14px'
          }}>
            Utilisez vos crédits pour organiser des compétitions. Chaque compétition coûte 1 crédit.
          </p>
        </div>
      </div>

      {/* Bandeau Don / Beta */}
      <div style={{ ...sectionStyle, border: '2px solid #3182ce', backgroundColor: '#ebf8ff' }}>
        <h3 style={{ ...sectionTitleStyle, color: '#2b6cb0' }}>
            <Heart size={20} color="#3182ce" fill="#3182ce" />
            Soutenir le projet
        </h3>
        <p style={{ color: '#2c5282', marginBottom: '20px', lineHeight: '1.6', fontSize: '15px' }}>
            Cette application n'a pas de vocation lucrative. Cependant, son développement demande du temps et le déploiement de ressources conséquentes. 
            <strong> N'hésitez pas à nous faire un don</strong> qui nous permettra de continuer ce projet et de le maintenir gratuit durant la phase bêta.
        </p>
        <button
          onClick={handleDonation}
          disabled={loading}
          style={{
            backgroundColor: '#3182ce',
            color: 'white',
            padding: '14px 28px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center', gap: '10px',
            width: '100%',
            justifyContent: 'center',
            boxShadow: '0 4px 6px rgba(49, 130, 206, 0.2)'
          }}
        >
          <Heart size={20} />
          Faire un don
        </button>
      </div>

      {/* Section Acheter des crédits */}
      <div style={{ ...sectionStyle, opacity: 0.6, pointerEvents: 'none' }}>
        <h3 style={sectionTitleStyle}>
          <CreditCard size={20} color="#48bb78" />
          Acheter des crédits (Désactivé en Bêta)
        </h3>

        <p style={{ color: '#4a5568', marginBottom: '20px', lineHeight: '1.5' }}>
          Chaque compétition coûte 1 crédit. Achetez des packs pour bénéficier de réductions !
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          {/* Offre Découverte */}
          <div style={{
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f7fafc',
            transition: 'border-color 0.2s'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Pack Découverte</h4>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#48bb78', marginBottom: '5px' }}>1</div>
            <div style={{ color: '#718096', marginBottom: '15px' }}>crédit</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2d3748', marginBottom: '15px' }}>12€</div>
            <div style={{ color: '#718096', fontSize: '14px', marginBottom: '15px' }}>12€ par crédit</div>
            <button
              onClick={() => handlePurchaseCredits('discovery')}
              disabled={loading}
              style={{
                backgroundColor: '#48bb78',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                width: '100%',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Chargement...' : 'Acheter'}
            </button>
          </div>

          {/* Offre Standard */}
          <div style={{
            border: '2px solid #3182ce',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#ebf8ff',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#3182ce',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              POPULAIRE
            </div>
            <h4 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Pack Standard</h4>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3182ce', marginBottom: '5px' }}>3</div>
            <div style={{ color: '#718096', marginBottom: '15px' }}>crédits</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2d3748', marginBottom: '15px' }}>30€</div>
            <div style={{ color: '#718096', fontSize: '14px', marginBottom: '15px' }}>10€ par crédit</div>
            <button
              onClick={() => handlePurchaseCredits('standard')}
              disabled={loading}
              style={{
                backgroundColor: '#3182ce',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                width: '100%',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Chargement...' : 'Acheter'}
            </button>
          </div>

          {/* Offre Premium */}
          <div style={{
            border: '2px solid #805ad5',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#faf5ff'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>Pack Premium</h4>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#805ad5', marginBottom: '5px' }}>6</div>
            <div style={{ color: '#718096', marginBottom: '15px' }}>crédits</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2d3748', marginBottom: '15px' }}>55€</div>
            <div style={{ color: '#718096', fontSize: '14px', marginBottom: '15px' }}>9,17€ par crédit</div>
            <button
              onClick={() => handlePurchaseCredits('premium')}
              disabled={loading}
              style={{
                backgroundColor: '#805ad5',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                width: '100%',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Chargement...' : 'Acheter'}
            </button>
          </div>
        </div>

        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#fefcbf',
          border: '1px solid #f6e05e',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '14px', color: '#744210', fontWeight: '500' }}>
            💡 <strong>À propos des crédits :</strong> Ils sont utilisés uniquement pour créer des compétitions.
            Aucun abonnement requis - payez seulement quand vous organisez !
          </div>
        </div>
      </div>

      {/* Section Sécurité */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>
          <Lock size={20} color="#e53e3e" />
          Sécurité du compte
        </h3>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Nouveau mot de passe</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              placeholder="Votre nouveau mot de passe"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#718096'
              }}
            >
              {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Confirmer le nouveau mot de passe</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              placeholder="Confirmez votre nouveau mot de passe"
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#718096'
              }}
            >
              {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          onClick={handleChangePassword}
          disabled={passwordLoading}
          style={{
            backgroundColor: passwordLoading ? '#a0aec0' : '#e53e3e',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: passwordLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Lock size={16} />
          {passwordLoading ? 'Modification...' : 'Changer le mot de passe'}
        </button>
      </div>

      {/* Section Zone de danger */}
      <div style={{ ...sectionStyle, border: '1px solid #feb2b2', backgroundColor: '#fff5f5' }}>
        <h3 style={{ ...sectionTitleStyle, color: '#c53030' }}>
          <Trash2 size={20} />
          Suppression du compte
        </h3>
        
        <p style={{ color: '#744210', fontSize: '14px', marginBottom: '20px' }}>
          La suppression de votre compte est <strong>définitive et irréversible</strong>. 
          Elle entraînera la suppression immédiate de toutes vos compétitions, athlètes, catégories et matchs.
        </p>

        <button
          onClick={async () => {
            const captchaCode = Math.random().toString(36).substring(2, 8).toUpperCase();
            const { value: userInput } = await Swal.fire({
                title: 'Supprimer votre compte ?',
                html: `
                    <div style="text-align: center;">
                        <p style="color: #e53e3e; font-weight: bold;">ATTENTION : TOUTES VOS DONNÉES SERONT EFFACÉES.</p>
                        <p style="margin: 15px 0;">Veuillez recopier le code suivant pour confirmer la suppression :</p>
                        <div style="background: #edf2f7; padding: 10px; font-family: monospace; font-size: 24px; letter-spacing: 5px; font-weight: bold; border-radius: 8px; margin-bottom: 20px;">
                            ${captchaCode}
                        </div>
                    </div>
                `,
                input: 'text',
                inputPlaceholder: 'Entrez le code ici...',
                showCancelButton: true,
                confirmButtonText: 'SUPPRIMER MON COMPTE DÉFINITIVEMENT',
                confirmButtonColor: '#e53e3e',
                cancelButtonText: 'Annuler',
                inputValidator: (value) => {
                    if (!value) return 'Vous devez saisir le code !';
                    if (value.toUpperCase() !== captchaCode) return 'Le code est incorrect.';
                }
            });

            if (userInput) {
                try {
                    Swal.fire({ title: 'Suppression en cours...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                    
                    const { data: { session: authSession }, error: sessionError } = await supabase.auth.getSession();
                    
                    if (sessionError || !authSession) {
                        throw new Error("Session introuvable, veuillez vous reconnecter.");
                    }

                    console.log("Token JWT présent:", !!authSession.access_token);

                    const response = await fetch('https://icaqghpgcoapikttehjg.supabase.co/functions/v1/delete-user-account', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authSession.access_token}`,
                            'apikey': 'sb_publishable_7nFecSfzP1uxlrIRKOwSJA_zIf8VfgR',
                            'x-client-info': 'savatevent-web'
                        },
                        body: JSON.stringify({ userId: authSession.user.id })
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || "Erreur lors de la suppression");
                    }

                    await supabase.auth.signOut();
                    Swal.fire('Compte supprimé', 'Votre compte et vos données ont été effacés.', 'success');
                } catch (err) {
                    Swal.fire('Erreur', translateError(err), 'error');
                }
            }
          }}
          style={{
            backgroundColor: 'white',
            color: '#e53e3e',
            padding: '12px 24px',
            border: '1px solid #e53e3e',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            justifyContent: 'center'
          }}
        >
          <Trash2 size={16} />
          Supprimer mon compte définitivement
        </button>
      </div>
    </div>
  );
}
